import { Modal } from '@/components/common/dialog';
import { useOrientation } from '@/hooks';
import type { BuildingInStoreInfo } from '@/services/dto';
import type { PoiContentSlide } from '@/types/shop';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import styles from './styles.module.scss';
import { useConfigStore } from '@/stores';
import PoiDetailVertical from '@/components/feature/shop/shop-poi-modal/poi-detail-vertical';
import PoiDetailHorizontal, { buildPoiDetailTabOrders } from '@/components/feature/shop/shop-poi-modal/poi-detail-horizontal';
import { formatPoiSlideToDetail } from '@/utils/poi/formatPoiSlideToDetail';

interface ShopPoiModalProps {
    isOpen: boolean;
    language: string;
    poiContent: PoiContentSlide | null;
    buildings: BuildingInStoreInfo[];
    screenMode: 'split' | 'fullscreen';
    onClose: () => void;
    onGuideStart: () => void;
    onLocationCheck: () => void;
}

const ShopPoiModal = ({ isOpen, language, poiContent, screenMode, onClose, onGuideStart, onLocationCheck }: ShopPoiModalProps) => {
    const { isHorizontal } = useOrientation();
    const prevActiveElementRef = useRef<HTMLElement | null>(null);
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const poiDetailFormat = useMemo(() => formatPoiSlideToDetail(poiContent, language), [poiContent, language]);

    const tabOrders = poiDetailFormat ? buildPoiDetailTabOrders(poiDetailFormat) : null;

    if (!poiDetailFormat) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            closeTabIndex={tabOrders?.close}
            closeRegionOrder={tabOrders?.regionOrder.close}
            className={clsx(styles['poi-modal'], {
                [styles['poi-modal__vertical']]: !isHorizontal,
                [styles['poi-modal__horizontal']]: isHorizontal,
                [styles['poi-modal__vertical--halfmode']]: screenMode === 'split' && !isHorizontal,
                [styles['poi-modal__contrast']]: contrast,
            })}
            scrollBarClassName="max-h-full! overflow-y-hidden!"
            wrapperClassName="items-center! pb-0!"
        >
            {isHorizontal ? (
                <PoiDetailHorizontal poiDetail={poiDetailFormat} language={language} onGuideStart={onGuideStart} onLocationCheck={onLocationCheck} />
            ) : (
                <PoiDetailVertical poiDetail={poiDetailFormat} language={language} onGuideStart={onGuideStart} onLocationCheck={onLocationCheck} />
            )}
        </Modal>
    );
};

export default ShopPoiModal;
